import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import DetailActions from "@/pages/detail/components/DetailActions";
import PlayerPlaceholder from "@/pages/detail/components/PlayerPlaceholder";
import SectionTitle from "@/components/base/SectionTitle";
import VideoGrid from "@/components/base/VideoGrid";
import EmptyState from "@/components/base/EmptyState";
import { getRelated, getVideoById } from "@/lib/videoQuery";
import { formatRating, formatViews, typeLabel } from "@/lib/format";

export default function DetailPage() {
  const { id = "" } = useParams();
  const video = getVideoById(id);

  useEffect(() => {
    if (!video) return;
    document.title = `${video.title}_免费在线观看 - 91视频在线免费观看`;
  }, [video]);

  if (!video) {
    return (
      <div className="mx-auto max-w-site px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-file-unknow-line"
          title="未找到该内容"
          description="你访问的内容不存在或已下线，返回首页看看其他精彩内容吧。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const related = getRelated(video, 5);

  const infoRows = [
    { label: "导演", value: video.director },
    { label: "主演", value: video.actors.join("、") },
    { label: "类型", value: video.genres.join(" / ") },
    { label: "地区", value: video.region },
    { label: "年份", value: `${video.year}` },
    { label: "状态", value: `${video.status} · ${video.episodes}` },
    { label: "更新", value: video.updateDate },
  ];

  return (
    <div>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={video.backdrop}
          alt={`${video.title} 大图`}
          title={`${video.title} 在线观看`}
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/60" />
        <div className="relative mx-auto max-w-site px-4 py-8 md:px-6 md:py-12">
          <nav className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-background-300" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link
              to={`/category/${video.type}`}
              className="transition-colors hover:text-accent-300"
            >
              {typeLabel(video.type)}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">{video.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:items-end">
            <div className="h-56 w-40 shrink-0 overflow-hidden rounded-lg border border-background-50/20 bg-secondary-900 md:h-72 md:w-52">
              <img
                src={video.cover}
                alt={`${video.title} 封面海报`}
                title={`${video.title} 在线观看`}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-background-50">
                  {typeLabel(video.type)}
                </span>
                <span className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-100">
                  {video.status}
                </span>
              </div>
              <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
                {video.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400" />
                  {formatRating(video.rating)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-play-circle-line" />
                  {formatViews(video.views)} 播放
                </span>
                <span>{video.year}</span>
                <span>{video.region}</span>
              </div>
              <div className="mt-5">
                <DetailActions video={video} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-site px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="space-y-10 lg:col-span-2">
            <section>
              <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
              <p className="text-sm leading-relaxed text-foreground-700">{video.synopsis}</p>
            </section>

            <PlayerPlaceholder video={video} />
          </div>

          <aside>
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="mb-4 font-heading text-base font-bold text-foreground-950">基本信息</h2>
              <dl className="space-y-3">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <dt className="w-12 shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="min-w-0 text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <SectionTitle
            title="相关推荐"
            subtitle="根据分类与主题为你推荐的相似内容"
            moreTo={`/category/${video.type}`}
          />
          {related.length > 0 ? (
            <VideoGrid videos={related} />
          ) : (
            <EmptyState icon="ri-movie-line" title="暂无相关推荐" />
          )}
        </section>
      </div>
    </div>
  );
}